import axios from "axios";
const url = "https://gympods-server.onrender.com";
//  const url = 'http://localhost:5001';
// const url = 'https://gympods-dev-server.onrender.com';

// Modify alert: show the message in the strip at the bottom of the window
// (#alert, public/index.html) until it's closed, with its close button or
// Escape. A new message replaces the one showing. #alert-text is a live
// region, but one that has only just been shown doesn't announce what it
// already holds, so the text is set again a moment later for screen readers.
let alertAnnounce;
window.alert = function (message) {
  const alert = document.getElementById('alert');
  const text = document.getElementById('alert-text');
  text.innerText = message;
  alert.style.display = 'flex';
  clearTimeout(alertAnnounce);
  alertAnnounce = setTimeout(() => { text.innerText = message; }, 100);
}
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') document.getElementById('alert').style.display = 'none';
});

/***********  User functions ***********/

// Safely write a single localStorage key. Mobile Safari Private mode throws
// QuotaExceededError on every set, which was one of the strand mechanisms
// behind the signup spinner hang — never let a storage write reach the caller.
function _safeSetLocal(key, value) {
  try {
    if (value !== undefined && value !== null) localStorage.setItem(key, value);
  } catch (e) { /* storage unavailable; carry on */ }
}
function _safeSetSession(key, value) {
  try {
    if (value !== undefined && value !== null) sessionStorage.setItem(key, value);
  } catch (e) { /* storage unavailable; carry on */ }
}
function _safeRemoveLocal(key) {
  try { localStorage.removeItem(key); } catch (e) { /* storage unavailable; carry on */ }
}

/***********  Staying signed in ***********/

// The member's "Keep me signed in" choice at their last log-in on this browser: '1'
// ticked, '0' unticked. Absent after sign-up, the email link and the app hand-off,
// which last until the browser closes and are never revoked.
const KEEP_KEY = 'gp_keep_signed_in';
// Session cookie (no Expires, so the browser drops it when it closes) holding only "1",
// never the token: this browser session has a signed-in member, so new tabs restore it.
const SIGNED_IN_COOKIE = 'gp_signed_in';
const COOKIE_ATTRS = 'Path=/; SameSite=Lax; Secure';
// Signed out when the browser session ends: the member's keys. The browser's own keys
// (gp_has_account, gp_attribution, gp_attribution_reported, gclid, gcl_aw) stay.
const MEMBER_KEYS = ['userSession', 'name', 'email', KEEP_KEY, 'home_site', 'newUser', 'strengthCredits',
  'cardioCredits', 'buddyCredits', 'ptCredits', 'lastPurchase', 'day', 'week'];
const REVOKE_TIMEOUT_MS = 10000;

function _setSignedInCookie() {
  try { document.cookie = `${SIGNED_IN_COOKIE}=1; ${COOKIE_ATTRS}`; } catch (e) { /* cookies blocked */ }
}
function _hasSignedInCookie() {
  try { return document.cookie.split(/;\s*/).includes(`${SIGNED_IN_COOKIE}=1`); } catch (e) { return false; }
}
function _clearSignedInCookie() {
  try { document.cookie = `${SIGNED_IN_COOKIE}=; Max-Age=0; ${COOKIE_ATTRS}`; } catch (e) { /* cookies blocked */ }
}
// Only called when the cookie is absent: set it, read it back, remove it.
function _cookiesWork() {
  _setSignedInCookie();
  const works = _hasSignedInCookie();
  _clearSignedInCookie();
  return works;
}

// Every token write. keep: true/false from the log-in box, null for sign-up, the email
// link and the app hand-off. Callers keep their own gp_has_account write.
function saveSignIn(token, keep) {
  if (!token) return;
  _safeSetLocal('userSession', token);
  _safeSetSession('userSession', token);
  if (keep === true) _safeSetLocal(KEEP_KEY, '1');
  else if (keep === false) _safeSetLocal(KEEP_KEY, '0');
  else _safeRemoveLocal(KEEP_KEY);
  if (keep !== true) _setSignedInCookie();
}

// Whether the log-in box starts ticked: this browser's last log-in ticked it and the
// member hasn't logged out since.
function keptSignedIn() {
  try { return localStorage.getItem(KEEP_KEY) === '1'; } catch (e) { return false; }
}

// Once per tab, before the router starts (main.js); returns whether this tab is signed in.
// The guard, nav and checkout read the per-tab sessionStorage copy of the token, so a new
// tab restores it from localStorage if the member ticked the box or this browser session
// is still open (the cookie). Otherwise that browser session has ended: sign out on this
// browser and, for an unticked log-in, revoke its token in the background. If cookies
// don't work here, change nothing: this tab starts signed out and other tabs carry on.
function restoreSignIn() {
  let token;
  let saved;
  let keep;
  try {
    token = sessionStorage.getItem('userSession');
    saved = localStorage.getItem('userSession');
    keep = localStorage.getItem(KEEP_KEY);
  } catch (e) { return false; }
  if (!token && saved) {
    if (keep === '1' || _hasSignedInCookie()) {
      try { sessionStorage.setItem('userSession', saved); token = saved; } catch (e) { /* storage unavailable */ }
    } else if (_cookiesWork()) {
      MEMBER_KEYS.forEach(_safeRemoveLocal);
      if (keep === '0') {
        axios.post(`${url}/login/logout`, {}, {
          timeout: REVOKE_TIMEOUT_MS,
          headers: { Authorization: `Bearer ${saved}` }
        }).catch(() => { /* best effort: the token still expires in 30 days */ });
      }
    }
  }
  // A tab still holding a token means this browser session is alive.
  if (token && keep !== '1') _setSignedInCookie();
  return !!token;
}

// Default network timeout (ms) for auth calls so a hung backend fails into
// the caller's error path rather than spinning forever.
const AUTH_TIMEOUT_MS = 20000;
// Cap on the fallback POST /pt at sign-up (only against a backend that doesn't
// make the PT profile itself), so a slow /pt can't strand the user.
const PT_PROFILE_TIMEOUT_MS = 8000;

// Build the user-facing message for a system-level failure (network, timeout,
// 5xx, unknown). Diagnostic code is included inline so support can identify
// the class of failure from a screenshot without needing the console.
function _systemErrorMessage(flow, code) {
  if (flow === 'register') {
    return `We couldn't create your account just now. Email info@gympods.com with code ${code} and we'll sort it out.`;
  }
  return `We couldn't log you in just now. Try again, or email info@gympods.com with code ${code}.`;
}

// Classify an axios error into a user-facing message and (for system errors)
// a diagnostic code. User-fixable cases (400-499) return no code. The raw
// error has already been console.error'd by the caller — this function only
// shapes what the user sees.
//
// Limitation: duplicate-email detection is regex-coupled to the server's
// current wording ("...already registered..."). If the backend rewords the
// 400 response, the user falls through to the generic 400 line instead of
// the targeted "try logging in" copy.
function _classifyAuthError(flow, err) {
  const prefix = flow === 'register' ? 'GP-REG' : 'GP-LOG';
  const response = err && err.response;
  if (!response) {
    if (err && err.code === 'ECONNABORTED') {
      const code = `${prefix}-TIMEOUT`;
      return { message: _systemErrorMessage(flow, code), code };
    }
    const code = `${prefix}-NET`;
    return { message: _systemErrorMessage(flow, code), code };
  }
  const status = response.status;
  if (status >= 500) {
    const code = `${prefix}-500`;
    return { message: _systemErrorMessage(flow, code), code };
  }
  if (status >= 400 && status < 500) {
    if (flow === 'register') {
      const serverMessage = response.data && response.data.message;
      if (typeof serverMessage === 'string' && /already registered/i.test(serverMessage)) {
        return { message: 'You already have an account.', alreadyRegistered: true };
      }
      return { message: "Something in your details didn't go through. Check them and try again." };
    }
    return { message: "That email and password don't match. Try again, or reset your password." };
  }
  const code = `${prefix}-ERR`;
  return { message: _systemErrorMessage(flow, code), code };
}

// Short: it runs while the member waits on step 1's Next.
const CHECK_EMAIL_TIMEOUT_MS = 4000;

// Is this email already registered? true / false, or null when the check
// couldn't be made (network, timeout, 400, 429, 5xx). Callers MUST treat null
// as "unknown, let them carry on": the /register 400 stays the backstop.
async function checkEmailRegistered(email) {
  try {
    const response = await axios.post(`${url}/register/check-email`, { email }, { timeout: CHECK_EMAIL_TIMEOUT_MS });
    const exists = response && response.data && response.data.exists;
    return typeof exists === 'boolean' ? exists : null;
  } catch (err) {
    console.warn('check-email unavailable:', { status: err?.response?.status, code: err?.code });
    return null;
  }
}

// Register a user.
//
// Returns a structured result so the caller controls navigation, spinner reset
// and error rendering — never navigates or pushes analytics from here. Every
// access to the response body is guarded; a missing field can no longer throw
// past `await register(...)` and strand the user on a spinner while the
// account exists server-side.
async function register(name, email, password, confirmPassword, home_site, address, postcode, mobile, emergancy_contact, pt) {
  if (password !== confirmPassword) {
    return { success: false, message: 'Please check your details and try again.' };
  }
  const attribution = _readAttribution();
  const data = {
    name,
    email,
    password,
    home_site,
    address,
    postcode,
    mobile,
    emergancy_contact,
    ...(pt === true ? { pt: true } : {}),
    ...(attribution ? { attribution } : {})
  };
  let response;
  try {
    response = await axios.post(`${url}/register`, data, { timeout: AUTH_TIMEOUT_MS });
  } catch (err) {
    console.error('register failed:', { status: err?.response?.status, message: err?.response?.data?.message, code: err?.code, err });
    const { message, code, alreadyRegistered } = _classifyAuthError('register', err);
    const result = code ? { success: false, message, code } : { success: false, message };
    if (alreadyRegistered) result.alreadyRegistered = true;
    return result;
  }
  if (response.status !== 200 && response.status !== 201) {
    console.error('register non-2xx:', { status: response.status, message: response?.data?.message, data: response?.data });
    // axios resolves only on 2xx by default, so this branch is effectively
    // unreachable. Treat as a system error if it ever fires.
    const code = 'GP-REG-ERR';
    return { success: false, message: _systemErrorMessage('register', code), code };
  }
  // Essential: persist the session so /book authenticates. Each write is
  // isolated — Mobile Safari Private mode throws on the first write and we
  // still want to continue to the success state.
  const token = response?.data?.Token;
  saveSignIn(token, null);
  if (token) _safeSetLocal('gp_has_account', '1');
  _safeSetLocal('name', response?.data?.User?.name);
  _safeSetLocal('email', response?.data?.User?.email);
  _safeSetLocal('home_site', home_site);
  _safeSetLocal('newUser', 'true');
  // Shared with the backend's Conversions API event so Meta deduplicates the pixel copy.
  _safeSetSession('gp_meta_event_id', response?.data?.meta_event_id);
  // The backend makes the PT profile inside /register and says whether it did.
  // Fallback, only when the reply doesn't say (a backend without that change):
  // POST /pt as before, awaited before the caller navigates (a navigation
  // cancels an in-flight request), capped and never throwing.
  let ptProfileCreated = false;
  if (pt === true) {
    const said = response?.data?.ptProfileCreated;
    if (typeof said === 'boolean') ptProfileCreated = said;
    else {
      try { ptProfileCreated = (await createPTProfile(token)) === true; }
      catch (e) { console.warn('PT profile not created at sign-up:', e?.response?.status || e?.code || e); }
    }
  }
  return { success: true, pt: pt === true, ptProfileCreated };
}

// Verify email
// The link in the verify emails. The link's code goes in the body, never the request's path, so it
// stays out of request logs; and it is never logged here.
// Resolves { status: 'verified', token?, pt } (token: a fresh session, on the request that
// verified the member; pt: true for a PT's account), { status: 'already', pt },
// { status: 'link_expired' | 'link_invalid' }, or { status: 'error' } (network or server
// trouble: worth trying again).
async function verifyEmailLink(linkToken) {
  try {
    const response = await axios.post(`${url}/register/verify-email/link`, { token: linkToken }, { timeout: AUTH_TIMEOUT_MS });
    const data = response.data || {};
    // pt: a PT's account (the backend checks the sign-up tick and the PT profile).
    const pt = data.pt === true;
    if (data.already) return { status: 'already', pt };
    if (data.verified) return { status: 'verified', token: data.token, pt };
    return { status: 'error' };
  } catch (err) {
    const code = err && err.response && err.response.data && err.response.data.code;
    if (code === 'link_expired' || code === 'link_invalid') return { status: code };
    console.error('verify email failed:', { status: err?.response?.status, code: err?.code });
    return { status: 'error' };
  }
}

// "Send me a new link" on the expired-link page: the server sends a fresh link to the
// email on the member's record, and answers the same whatever happens.
// Resolves 'sent', 'limited' (one was sent in the last couple of minutes) or 'error'.
async function resendVerificationFromLink(linkToken) {
  try {
    await axios.post(`${url}/register/resend-verification/link`, { token: linkToken }, { timeout: AUTH_TIMEOUT_MS });
    return 'sent';
  } catch (err) {
    if (err?.response?.status === 429) return 'limited';
    console.error('resend verification failed:', { status: err?.response?.status, code: err?.code });
    return 'error';
  }
}

// "Resend email" for a signed-in member who hasn't verified.
// Resolves 'sent', 'already' (verified since), 'limited' or 'error'.
async function resendVerification() {
  let authToken;
  try { authToken = localStorage.userSession; } catch (e) { /* storage blocked */ }
  try {
    const response = await axios.post(`${url}/register/resend-verification`, {}, {
      headers: { Authorization: `Bearer ${authToken}` },
      timeout: AUTH_TIMEOUT_MS,
    });
    return response.data && response.data.already ? 'already' : 'sent';
  } catch (err) {
    if (err?.response?.status === 429) return 'limited';
    console.error('resend verification failed:', { status: err?.response?.status, code: err?.code });
    return 'error';
  }
}

// Login a user.
//
// Mirrors register(): structured result, guarded field access, post-login
// profile fetches treated as non-essential enrichment so a hiccup there
// never blocks navigation. Caller owns spinner reset and redirect.
async function login(email, password, keepSignedIn = false) {
  const attribution = _readTaggedAttribution();
  const data = { email, password, client: 'web', page: location.pathname, ...(attribution ? { attribution } : {}) };
  let response;
  try {
    response = await axios.post(`${url}/login`, data, { timeout: AUTH_TIMEOUT_MS });
  } catch (err) {
    console.error('login failed:', { status: err?.response?.status, message: err?.response?.data?.message, code: err?.code, err });
    const { message, code } = _classifyAuthError('login', err);
    return code ? { success: false, message, code } : { success: false, message };
  }
  if (response?.status !== 200) {
    console.error('login non-2xx:', { status: response?.status, message: response?.data?.message, data: response?.data });
    const code = 'GP-LOG-ERR';
    return { success: false, message: _systemErrorMessage('login', code), code };
  }
  const token = response?.data?.token;
  saveSignIn(token, keepSignedIn === true);
  if (token) _safeSetLocal('gp_has_account', '1');
  // The log-in was this tab's check-in.
  if (token) lastCheckinAt = Date.now();
  // This login already sent the click; don't resend it from startSession on /book.
  if (attribution) _safeSetLocal('gp_attribution_reported', _clickKey(attribution));
  _safeSetLocal('name', response?.data?.resName);
  _safeSetLocal('email', response?.data?.resEmail);
  // Profile and PT-profile fetches are best-effort enrichment for the next
  // page. They were the strand mechanism on the login path before — a
  // missing field in either response used to throw past the redirect.
  // Both at once, kept for the member page that follows (loadAccount never rejects).
  clearAccount();
  await loadAccount();
  return { success: true };
}

// A 401: the token is dead. They had an account (expired or signed-out token), so the
// log-in form, not sign-up.
function _toLogIn() {
  sessionStorage.removeItem('userSession');
  localStorage.removeItem('userSession');
  window.location.href = '/join?show=login';
}

// Wait before the one retry of a failed profile load.
const PROFILE_RETRY_MS = 300;

// One profile request: { user } on success, else { user: null, retry } where retry says
// whether a second try might work.
async function _fetchProfile() {
  const name = localStorage.name;
  const authToken = localStorage.userSession;
  let status = null;
  const response = await axios.get(`${url}/register/${name}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`
    }
  }).catch((err) => {
    // err.response is undefined on network failures — guard it so the catch
    // itself doesn't throw, which would re-reject and propagate past callers
    // that treat profile fetch as best-effort.
    status = err?.response?.status ?? null;
    if (status === 401) _toLogIn();
    return null;
  });
  if (response) {
    const user = response.status === 200 ? response.data?.User : null;
    // A 200 without a user is the server's { Success: false }, e.g. a clash with
    // another save of the member at that moment.
    return { user: user || null, retry: response.status === 200 && !user };
  }
  // No response (network or timeout) or a server error. Not a 401 or other 4xx.
  return { user: null, retry: status === null || status >= 500 };
}

// Get user's profile: the user, or null if it couldn't be loaded (callers say so on
// the page). Tries once more, PROFILE_RETRY_MS later, when that might help.
async function getProfile() {
  let { user, retry } = await _fetchProfile();
  if (!user && retry) {
    await new Promise((resolve) => setTimeout(resolve, PROFILE_RETRY_MS));
    ({ user } = await _fetchProfile());
  }
  if (!user) return null;
  _safeSetLocal('newUser', user.isNewUser);
  _safeSetLocal('strengthCredits', user.strength_credit);
  _safeSetLocal('cardioCredits', user.cardio_credit);
  _safeSetLocal('buddyCredits', user.buddy_credit);
  _safeSetLocal('home_site', user.home_site);
  _safeSetSession('subscription', user.subscription && user.subscription > 0 ? 'true' : 'false');
  return user;
}

// The PT profile, or undefined when there isn't one. Tries once more, PROFILE_RETRY_MS
// later, when the call got no reply or a server error; throws if it still can't be read.
async function _fetchPTProfileWithRetry() {
  try {
    return await getPTProfile();
  } catch (err) {
    const status = err?.response?.status ?? null;
    if (status !== null && status < 500) throw err;
    await new Promise((resolve) => setTimeout(resolve, PROFILE_RETRY_MS));
    return getPTProfile();
  }
}

// The member pages' account: the profile and the PT profile at the same time, one load
// shared by every caller for ACCOUNT_REUSE_MS (main.js starts it before a member page's
// code has downloaded, /book when a location is tapped). Resolves { user, pt, ptFailed }:
// user is getProfile()'s answer (null when it couldn't be loaded, after its own retry and
// 401 handling), pt the PT profile (undefined when there isn't one), ptFailed when the PT
// profile couldn't be read, after one retry. Never rejects. A failed load isn't kept, and
// anything that changes the account clears it (clearAccount).
const ACCOUNT_REUSE_MS = 20000;
let accountLoad = null; // { promise, at, token }

function loadAccount() {
  let token = null;
  try { token = localStorage.userSession; } catch (e) { /* storage blocked */ }
  if (accountLoad && accountLoad.token === token && Date.now() - accountLoad.at < ACCOUNT_REUSE_MS) {
    return accountLoad.promise;
  }
  const promise = Promise.all([
    getProfile().catch(() => null),
    _fetchPTProfileWithRetry().then((pt) => ({ pt, ptFailed: false }), () => ({ pt: undefined, ptFailed: true })),
  ]).then(([user, { pt, ptFailed }]) => {
    if ((!user || ptFailed) && accountLoad && accountLoad.promise === promise) accountLoad = null;
    return { user, pt, ptFailed };
  });
  accountLoad = { promise, at: Date.now(), token };
  return promise;
}

function clearAccount() {
  accountLoad = null;
}
window.addEventListener('gp-auth-change', clearAccount);

// Which ad click a record is, by its tags rather than captured_at: the router
// re-captures the click (new captured_at) every time a tagged URL is reloaded.
function _clickKey(attribution) {
  return TAGGED_ATTRIBUTION_KEYS.map((key) => attribution[key] || '').join('|');
}

// Background visit check for a signed-in member (main.js). Calls POST /register/session
// at every page load, and when the tab comes back into view 30+ minutes after this
// tab's last check-in or with a tagged ad click not yet reported. The backend decides
// what counts as a return (30+ minutes since the member's last activity). Only a 401
// signs out (the token is dead): it clears the session keys, keeps everything else in
// storage (gp_attribution, gclid, /join progress, gp_has_account) and returns
// 'signed-out'. Network errors, timeouts and 5xx (a Render cold start) leave the member
// signed in and the last check-in time as it was, so the next view tries again.
const SESSION_TIMEOUT_MS = 10000;
const CHECKIN_GAP_MS = 30 * 60 * 1000;
let lastCheckinAt = 0; // this tab's last check-in or log-in; memory only
let sessionInFlight = false;

async function startSession({ atLoad }) {
  if (sessionInFlight) return;
  let token;
  let reported;
  try {
    token = sessionStorage.getItem('userSession');
    reported = localStorage.getItem('gp_attribution_reported');
  } catch (e) { return; }
  if (!token) return;
  const tagged = _readTaggedAttribution();
  const newClick = !!(tagged && _clickKey(tagged) !== reported);
  const due = Date.now() - lastCheckinAt >= CHECKIN_GAP_MS;
  if (!atLoad && !newClick && !due) return;
  sessionInFlight = true;
  try {
    await axios.post(`${url}/register/session`, { client: 'web', page: location.pathname, ...(tagged ? { attribution: tagged } : {}) }, {
      timeout: SESSION_TIMEOUT_MS,
      headers: { Authorization: `Bearer ${token}` }
    });
    lastCheckinAt = Date.now();
    if (tagged) _safeSetLocal('gp_attribution_reported', _clickKey(tagged));
  } catch (err) {
    if (err?.response?.status !== 401) return;
    ['userSession', 'name', 'email'].forEach((key) => {
      try { sessionStorage.removeItem(key); localStorage.removeItem(key); } catch (e) { /* storage unavailable */ }
    });
    window.dispatchEvent(new Event('gp-auth-change'));
    return 'signed-out';
  } finally {
    sessionInFlight = false;
  }
}

async function updateProfile(updates) {
  // send request to server
  const authToken = localStorage.userSession;
  const response = await axios.put(`${url}/register/update`, { updates }, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`
    }
  }).finally(clearAccount);
  // return profile if successful
  console.log(response);
  if (response && response.status == 200) {
    return response.data.updatedProfile;
  } else {
    alert("Unable to update profile. Please login and retry");
    return false;
  }
}

// Log out clears everything this site stores (sign-in, gp_attribution, /join
// progress...) except gp_has_account, so "Book a POD" still offers this member the
// log-in form rather than sign-up. Deleting the account clears it too.
function _clearStorageOnLogout() {
  clearAccount();
  try { sessionStorage.clear(); localStorage.clear(); } catch (e) { /* storage unavailable */ }
  _clearSignedInCookie();
  _safeSetLocal('gp_has_account', '1');
}

// Logout user
async function logout() {
  // send request to server
  const authToken = localStorage.getItem("userSession");
  // create axios instance with full security
  const instance = axios.create({
    baseURL: `${url}`,
    headers: {
      "Access-Control-Allow-Origin": "*",
      Authorization: `Bearer ${authToken}`
    },
    withCredentials: true,
    credentials: "same-origin"
  });
  try {
    // check for auth token
    if (authToken === null) {
      alert("No user is authenticated, please login!!!");
    } else {
      // post request to server
      const response = await instance.post(`/login/logout`).catch(() => {
        _clearStorageOnLogout();
        window.location.href = '/';
      });
      if (response.status === 200) {
        // delete user's session
        _clearStorageOnLogout();
        // inform user of success
        window.location.href = '/';
      }
    }
  } catch (error) {
    console.log(error);
  }
}

// Request the resetting of a password
async function forgotPassword(email) {
  if (!email) alert('Please enter your email');
  else {
    alert('Please check your email for instructions');
    const data = {
      email
    };
    // send request to server
    const response = await axios.post(`${url}/register/forgot_password`, data);
    console.log(response);
  }
}

// Reset the password
// TODO: use URL params to get token, then call function w/ token
async function resetPassword(token, new_password, confirm_password) {
  const data = {
    new_password,
    confirm_password
  };
  const response = await axios.post(`${url}/register/reset_password/${token}`, data);
  console.log(response);
  alert(response.data.message);
  return response.data;
}

// Delete account
async function deleteAccount() {
  // send request to server
  const authToken = localStorage.userSession;
  const response = await axios.delete(`${url}/register`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`
    }
  });
  // return profile if successful
  console.log(response);
  if (response && response.status == 200) {
    // Same as Log out: the dead token and the gp_has_account marker must not survive.
    try { sessionStorage.clear(); localStorage.clear(); } catch (e) { /* storage unavailable */ }
    _clearSignedInCookie();
    window.location.href = '/join';
  } else {
    alert("Unable to delete account. Please login and retry");
    return false;
  }
}

/***********  PT functions ***********/

// Create a PT profile
async function createPTProfile(token) {
  let authToken = token;
  if (!authToken) {
    try { authToken = localStorage.userSession; } catch (e) { /* storage blocked */ }
  }
  const instance = axios.create({
    baseURL: `${url}`,
    headers: {
      "Access-Control-Allow-Origin": "*",
      Authorization: `Bearer ${authToken}`
    },
    withCredentials: true,
    credentials: "same-origin"
  });
  const response = await instance.post(`/pt`, undefined, { timeout: PT_PROFILE_TIMEOUT_MS });
  console.log(response);
  return response.data.success;
}

// Get own PT profile
async function getPTProfile() {
  const authToken = localStorage.userSession;
  const instance = axios.create({
    baseURL: `${url}`,
    headers: {
      "Access-Control-Allow-Origin": "*",
      Authorization: `Bearer ${authToken}`
    },
    withCredentials: true,
    credentials: "same-origin"
  });
  const response = await instance.get(`/pt/${localStorage.email}`);
  console.log('PT Profile', response);
  const ok = response?.data?.success === true;
  const profile = response?.data?.profile;
  if (ok && profile?.verified === true) {
    _safeSetSession('isPT', 'true');
    _safeSetSession('isPTPending', 'false');
    _safeSetLocal('ptCredits', profile.pod_credit);
  } else if (ok && profile?.verified === false) {
    _safeSetSession('isPTPending', 'true');
    _safeSetSession('isPT', 'false');
  } else {
    _safeSetSession('isPTPending', 'false');
    _safeSetSession('isPT', 'false');
  }
  return profile;
}

// Export visits for a PT
async function exportVisits(start, end) {
  const authToken = localStorage.userSession;
  const instance = axios.create({
    baseURL: `${url}`,
    headers: {
      "Access-Control-Allow-Origin": "*",
      Authorization: `Bearer ${authToken}`
    },
    withCredentials: true,
    credentials: "same-origin"
  });
  const response = await instance.post(`/pt/export`, {
    start,
    end
  });
  console.log(response);
  return response?.data?.visits || false;
}

// Get all PT listings
async function getPTListings() {
  const response = await axios.get(`${url}/pt/listings/all`);
  console.log(response);
  return response.data.listings;
}

// Contact a PT
async function contactPT(name, email, message, pt) {
  const data = {
    name,
    email,
    message,
    pt
  };
  // send request to server. A non-2xx (400, 429, 503) throws; the modal shows its error for both.
  const response = await axios.post(`${url}/pt/contact`, data);
  return response.data.success === true;
}

// Claim a free PT session (/free-pt-session → POST /pt/free-session-claim).
// Sends the stored ad click, the same record /register gets. The backend
// answers 201 { success: true } for every valid claim; anything else throws.
async function claimFreePtSession(claim) {
  const attribution = _readAttribution();
  const response = await axios.post(`${url}/pt/free-session-claim`, {
    ...claim,
    ...(attribution ? { attribution } : {}),
  });
  return response.status === 201 && response.data && response.data.success === true;
}

// ─── Register-interest submission (/gym-space-to-rent → POST /pt/listings/apply)
//
// The backend handler enforces name ≥ 2, valid email, location enum
// [putney|dalston|both], link (empty string OK) and bio 10..5000. Joi
// rejects unknown keys. The client-side form is now ALL-OPTIONAL — the
// encoder here bridges to those constraints without touching the backend:
//
//   • name empty            → "(not provided)"
//   • email empty           → "no-email-provided@gympods.com"
//   • location empty        → "both" (truth preserved in the bio block)
//   • location Putney|Dalston|Both → putney|dalston|both
//   • disciplines (array)   → dropped from the payload, folded into the bio,
//                             joined with ", " (one pick reads as it always did)
//   • link                  → normalized: http(s)://… passes through; a bare
//                             domain / www-form gets an https:// prefix; an
//                             @handle or any free text sends link:"" and moves
//                             the raw value into the bio as a "Website /
//                             Instagram:" line. Backend requires a valid
//                             http(s) URI or "" — a placeholder that invites
//                             "@handle" otherwise 400s.
//   • findAPro Yes|No|empty → dropped from the payload, folded into the bio
//   • bio                   → structured lines guaranteeing ≥ 10 chars:
//                              Discipline: <value(s) or 'not stated'>
//                              Wants a Find a Pro listing: <Yes | No | 'not stated'>
//                              Website / Instagram: <raw>          (only when link couldn't be normalized)
//                              Preferred location: <label or 'not stated'>  (only when we defaulted the sent location)
//                              (blank line)
//                              <bio text or '(no message left)'>
//
// Keeps pt@gympods.com's notification email human-readable and carries any
// field we couldn't send through the fixed 5-key pipeline.
const PRO_LOCATION_MAP = {
  Putney: 'putney',
  Dalston: 'dalston',
  Both: 'both',
};

// Return { link, bioLine }. Never throws — anything the backend won't
// accept as a URI is preserved verbatim in bioLine so the notification
// email still carries the raw value.
function normalizeProLink(trimmed) {
  if (!trimmed) return { link: '', bioLine: null };
  if (/^https?:\/\//i.test(trimmed)) {
    return { link: trimmed, bioLine: null };
  }
  const looksLikeDomain =
    !trimmed.startsWith('@') &&
    !/\s/.test(trimmed) &&
    trimmed.includes('.');
  if (looksLikeDomain) {
    return { link: `https://${trimmed}`, bioLine: null };
  }
  return { link: '', bioLine: `Website / Instagram: ${trimmed}` };
}

function buildProApplyPayload(form) {
  const name = (form.name || '').trim();
  const email = (form.email || '').trim();
  const rawLink = (form.link || '').trim();
  const bioText = (form.bio || '').trim();
  const discipline = (Array.isArray(form.disciplines) ? form.disciplines : [])
    .map((d) => (d || '').trim())
    .filter(Boolean)
    .join(', ');
  const findAPro = form.findAPro === 'Yes' || form.findAPro === 'No' ? form.findAPro : '';
  const rawLocation = form.location || '';
  const mappedLocation = PRO_LOCATION_MAP[rawLocation];
  const { link, bioLine: linkBioLine } = normalizeProLink(rawLink);

  const bioLines = [
    `Discipline: ${discipline || 'not stated'}`,
    `Wants a Find a Pro listing: ${findAPro || 'not stated'}`,
  ];
  if (linkBioLine) {
    bioLines.push(linkBioLine);
  }
  if (!mappedLocation) {
    bioLines.push('Preferred location: not stated');
  }
  bioLines.push('');
  bioLines.push(bioText || '(no message left)');

  return {
    name: name || '(not provided)',
    email: email || 'no-email-provided@gympods.com',
    location: mappedLocation || 'both',
    link,
    bio: bioLines.join('\n'),
  };
}

async function submitProApplication(form) {
  const payload = buildProApplyPayload(form);
  try {
    const response = await axios.post(`${url}/pt/listings/apply`, payload);
    return response.data.success;
  } catch (err) {
    // Backend 400s return {success:false, message:"..."} — preserve status
    // + message so the view can render the Joi message inline. Network /
    // 5xx fall through to the caller's generic branch.
    const status = err?.response?.status;
    const data = err?.response?.data;
    const backendMessage =
      (data && typeof data === 'object' && (data.message || data.error))
      || (typeof data === 'string' && data)
      || '';
    const shaped = new Error(backendMessage || err.message || 'Request failed');
    shaped.status = status;
    shaped.backendMessage = backendMessage;
    throw shaped;
  }
}

// ─── Listing-update submission (Option C, temporary) ────────────────
//
// The backend `/pt/listings/apply` handler is a fixed 5-field pipeline
// (name / email / location-enum / link / bio) — see
// gympods-backend/controllers/pt_register.js. Joi rejects unknown keys,
// the Mongoose model has no home for the update-form's structured
// values, and the notification email is a fixed template that only
// prints those five fields.
//
// Until Oliver ships a dedicated `/pt/listings/update` endpoint
// (Option A in the migration batch), submissions from the update-your-
// listing view are ENCODED into the legacy shape here:
//
//   • location  ← checkbox multi-select → enum ("putney" / "dalston" / "both")
//   • link      ← "" (not collected on the update form)
//   • bio       ← human-readable, one-field-per-line summary of the
//                 update; first line is a triage marker so pt@gympods.com
//                 can tell an update apart from a new application at a glance.
//
// When Option A lands, delete BOTH `buildLegacyApplyPayload` and swap the
// axios call in `submitListingUpdate` to the new endpoint. That's the
// intent behind isolating the encoding here — future change is a clean
// deletion, not surgery.

// Backend's Joi cap on the `bio` field. buildLegacyApplyPayload throws
// LEGACY_BIO_TOO_LONG if the encoded bio would exceed this. The view
// checks the same limit via buildLegacyBio in validate() so the failure
// surfaces as an inline error on the New bio field rather than as a
// generic submit-failed state.
const LEGACY_BIO_MAX = 5000;

function buildLegacyLocation(update) {
  const locs = (update.locations || []).map((l) => (l || '').toLowerCase());
  const putney = locs.includes('putney');
  const dalston = locs.includes('dalston');
  // The view validates ≥ 1 location before this runs, so the empty branch
  // is defensive only. Kept explicit rather than a fallthrough default so
  // a future edit doesn't silently ship "both" for a truly empty state.
  return putney && dalston ? 'both'
    : putney               ? 'putney'
    : dalston              ? 'dalston'
    : 'both';
}

// Human-readable, one-field-per-line summary of the update. First line
// is a triage marker so pt@gympods.com can tell an update apart from a
// new application at a glance. Exposed separately from
// buildLegacyApplyPayload so the view can length-check pre-submit
// without duplicating the concat logic (see LEGACY_BIO_MAX guard).
function buildLegacyBio(update) {
  const yesNo = (v) => (v ? 'Yes' : 'No');

  // Professions line — named picks, then "Other (free text)" appended
  // if Other is ticked and the follow-up input has content.
  const professionsLine = (() => {
    const named = (update.professions || []).filter((p) => p !== 'Other');
    const otherText = (update.professionOther || '').trim();
    const other = (update.professions || []).includes('Other') && otherText
      ? `Other (${otherText})`
      : null;
    const parts = [...named, other].filter(Boolean);
    return parts.length ? parts.join(', ') : 'none selected';
  })();

  // Specialities line — list, then "; also: …" for anything-we-missed.
  const specialitiesLine = (() => {
    const list = (update.specialities || []).join(', ');
    const also = (update.specialityOther || '').trim();
    const base = list || 'none from list';
    return also ? `${base}; also: ${also}` : base;
  })();

  // Bio-update line varies with whether the Pro pasted new copy inline.
  // Empty-but-Yes is a legit state — Pro will email the copy separately.
  const newBioText = (update.newBio || '').trim();
  const bioLine = update.updateBio
    ? (newBioText ? 'Yes — new bio included' : 'Yes — will send by email')
    : 'No';

  const top = [
    "LISTING UPDATE — not a new application",
    "",
    `Professions: ${professionsLine}`,
    `Specialities: ${specialitiesLine}`,
    `Update headshot: ${yesNo(update.updateHeadshot)}`,
    `Update bio: ${bioLine}`,
    `Intro offer: ${yesNo(update.introOffer)}`,
    `Listing: ${update.keepListing ? 'Keep' : 'Remove'}`,
  ].join('\n');

  // "New bio:" block LAST — keeps the triage lines at the top of the
  // email regardless of how long the pasted bio is. Only appended when
  // Yes was chosen AND the textarea was actually filled.
  return update.updateBio && newBioText
    ? `${top}\n\nNew bio:\n${newBioText}`
    : top;
}

function buildLegacyApplyPayload(update) {
  const bio = buildLegacyBio(update);
  if (bio.length > LEGACY_BIO_MAX) {
    // View validates against LEGACY_BIO_MAX before calling this, so this
    // throw is a defensive backstop — carries the length so a caller in
    // the fallback path (or a future test) can render something useful.
    const err = new Error(`Encoded bio is ${bio.length} chars; backend caps at ${LEGACY_BIO_MAX}.`);
    err.code = 'LEGACY_BIO_TOO_LONG';
    err.encodedLength = bio.length;
    throw err;
  }
  return {
    name: update.name,
    email: update.email,
    location: buildLegacyLocation(update),
    link: '',
    bio,
  };
}

async function submitListingUpdate(update) {
  const legacy = buildLegacyApplyPayload(update);
  const response = await axios.post(`${url}/pt/listings/apply`, legacy);
  return response.data.success;
}

/***********  Bookings functions ***********/

// 15 s, then the booking page says it couldn't load the times (R2 #14).
const AVAILABILITY_TIMEOUT_MS = 15000;

// Get Pod availability JSON
async function getAvailability(site, type, week) {
  const response = await axios.get(`${url}/book?site=${site}&type=${type}&week=${week}`, { timeout: AVAILABILITY_TIMEOUT_MS });
  console.log(response);
  return response.data.availability;
}

// Book a Pod (covers all scenarios). Never throws: resolves { booked: true }, or
// { booked: false } with offline (no reply), signedOut (a 401: on the way to the log-in
// form, as getProfile does) or message (another 4xx's own words, e.g. "No credits
// available"). The server's 200 { success: false } refusals and any 5xx come back without
// a message, and the page says so in its own words (R1 #3, R2 #15, R2 #89).
async function bookPod(site, type, week, start, end, booking_type, staggered, pt, buddy_email) {
  console.log(type, week, start, end, booking_type, staggered, pt);
  const data = {
    site,
    type,
    week,
    start,
    end,
    booking_type,
    staggered,
    pt,
    buddy_email
  };
  const authToken = localStorage.userSession;
  const instance = axios.create({
    baseURL: `${url}`,
    headers: {
      "Access-Control-Allow-Origin": "*",
      Authorization: `Bearer ${authToken}`
    },
    withCredentials: true,
    credentials: "same-origin"
  });
  try {
    const response = await instance.post(`/book`, data).finally(clearAccount);
    console.log(response);
    return { booked: response?.data?.success === true };
  } catch (err) {
    const status = err?.response?.status;
    if (!status) return { booked: false, offline: true };
    if (status === 401) {
      _toLogIn();
      return { booked: false, signedOut: true };
    }
    if (status < 500) return { booked: false, message: err.response.data?.message || '' };
    return { booked: false };
  }
}

// Add to waitlist
async function joinWaitingList(site, type, msec_time) {
  const data = {
    site,
    type,
    msec_time
  };
  const authToken = localStorage.userSession;
  const instance = axios.create({
    baseURL: `${url}`,
    headers: {
      "Access-Control-Allow-Origin": "*",
      Authorization: `Bearer ${authToken}`
    },
    withCredentials: true,
    credentials: "same-origin"
  });
  const response = await instance.post(`/book/waitlist`, data);
  console.log(response);
  if (response.data.success === false) alert(response.data.message);
  else alert('Thank you for joining the waiting list. You will receive notification if the slot becomes available.');
}

// Add a buddy to an existing booking (costs 1 credit)
async function addBuddyToBooking(booking_id, buddy_email) {
  const authToken = localStorage.userSession;
  const instance = axios.create({
    baseURL: `${url}`,
    headers: {
      "Access-Control-Allow-Origin": "*",
      Authorization: `Bearer ${authToken}`
    },
    withCredentials: true,
    credentials: "same-origin",
    // 20 s, then the caller says it couldn't confirm the add.
    timeout: 20000
  });
  try {
    const response = await instance.put('/book/buddy', { booking_id, buddy_email }).finally(clearAccount);
    return response.data;
  } catch (err) {
    // Never the server's text: the caller says what went wrong.
    return {
      success: false,
      httpStatus: err.response?.status ?? null,
      offline: !err.response,
      timedOut: err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT'
    };
  }
}

// Cancel a booking
async function cancelPod(booking_id) {
  const data = {
    booking_id
  };
  const authToken = localStorage.userSession;
  const instance = axios.create({
    baseURL: `${url}`,
    headers: {
      "Access-Control-Allow-Origin": "*",
      Authorization: `Bearer ${authToken}`
    },
    withCredentials: true,
    credentials: "same-origin"
  });
  const response = await instance.post(`/book/cancel`, data).finally(clearAccount);
  console.log(response);
  alert(response.data.message);
  return response.data;
}

/***********  Payment functions ***********/

// Read the GA4 client_id from the `_ga` cookie. Format is
// `GA1.<version>.<random>.<timestamp>` — we return the trailing
// `<random>.<timestamp>` (the raw client_id GA4 uses in Measurement
// Protocol / server-side hits). Returns null when the cookie is absent
// or malformed so callers can omit the field entirely rather than send
// a null. Absence is the common case under Consent Mode v2 default-denied.
function _readGaClientId() {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const match = document.cookie.split('; ').find(row => row.startsWith('_ga='));
  if (!match) return null;
  const m = match.slice('_ga='.length).match(/^GA1\.\d+\.(\d+\.\d+)$/);
  return m ? m[1] : null;
}

// Build the `attribution` object for /register, /login and checkout: the record the router stored
// at landing (router/index.js, 30-day expiry) plus the Meta _fbc/_fbp cookies
// as they are now. Raw fbclid only — no fbc synthesis here. Returns null when
// there's nothing to send or on any error; attribution must never block sign-up.
function _readAttribution() {
  try {
    let attribution = {};
    const stored = JSON.parse(localStorage.getItem('gp_attribution') || 'null');
    if (stored && typeof stored === 'object' && Date.now() - Date.parse(stored.captured_at) < 30 * 24 * 60 * 60 * 1000) {
      attribution = { ...stored };
    }
    const cookies = document.cookie ? document.cookie.split('; ') : [];
    ['fbc', 'fbp'].forEach((key) => {
      const row = cookies.find(r => r.startsWith(`_${key}=`));
      if (row) attribution[key] = row.slice(key.length + 2);
    });
    return Object.keys(attribution).length ? attribution : null;
  } catch (e) { return null; }
}

// /login only records a returning visit from an ad or campaign link, so send
// the record only when it carries a tag; organic visits stay out.
const TAGGED_ATTRIBUTION_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid', 'ad_id'];

function _readTaggedAttribution() {
  const attribution = _readAttribution();
  return attribution && TAGGED_ATTRIBUTION_KEYS.some(key => attribution[key]) ? attribution : null;
}

// Secondary guard for begin_checkout. Primary guard is button-disable in the
// callers. This catches reload-mid-redirect and the /checkout?price_id= mount
// auto-fire in CheckoutView. Keyed by pack signature so a genuinely different
// pack still pushes; 10s window covers the click → Stripe redirect interval
// without blocking a real cancel-and-return cycle (which is always slower).
function pushBeginCheckoutOnce(pack) {
  const params = {
    pack_title: pack.title || 'Taster Pack',
    credits: pack.credits,
    value: pack.price || 18,
    currency: 'GBP',
    subscription: pack.subscription || false
  };
  const sig = `${params.pack_title}|${params.credits}|${params.value}|${params.subscription}`;
  try {
    const raw = sessionStorage.getItem('gp_begin_checkout_guard');
    if (raw) {
      const guard = JSON.parse(raw);
      if (guard && guard.sig === sig && Date.now() - guard.ts < 10000) return;
    }
    sessionStorage.setItem('gp_begin_checkout_guard', JSON.stringify({ sig, ts: Date.now() }));
  } catch (e) { /* sessionStorage unavailable; fall through and push */ }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'begin_checkout', ...params });
  try {
    localStorage.setItem('lastPurchase', JSON.stringify(params));
  } catch (e) { /* localStorage unavailable */ }
}

// Back from Stripe paid: the purchase event for the pack begin_checkout saved, once.
// My PODs pushes it, or the router when it sends the member on to their booking page.
function pushPurchaseOnce() {
  const lastPurchase = localStorage.getItem('lastPurchase');
  if (!lastPurchase) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'purchase',
    ...JSON.parse(lastPurchase)
  });
  localStorage.removeItem('lastPurchase');
  sessionStorage.removeItem('gp_begin_checkout_guard');
}

// 20 s, then the page says it couldn't open the payment page (R2 #14).
const CHECKOUT_TIMEOUT_MS = 20000;

async function createCheckout(selection, credits, home_site, price, subscription, pack) {
  const ga_client_id = _readGaClientId();
  const attribution = _readAttribution();
  const data = {
    selection,
    credits,
    home_site,
    price,
    subscription,
    gclid: localStorage.getItem('gclid') || undefined,
    ...(ga_client_id ? { ga_client_id } : {}),
    ...(attribution ? { attribution } : {})
  };
  const authToken = localStorage.userSession;
  const instance = axios.create({
    baseURL: url,
    headers: {
      "Access-Control-Allow-Origin": "*",
      Authorization: `Bearer ${authToken}`
    },
    withCredentials: true,
    credentials: "same-origin"
  });
  const response = await instance.post('/credit/checkout', data, { timeout: CHECKOUT_TIMEOUT_MS }).finally(clearAccount);
  console.log(response);
  if (response.data.success === true) {
    if (pack) pushBeginCheckoutOnce(pack);
    sessionStorage.selection = '';
    window.location.href = response.data.url;
    return true;
  } else if (response.data.message) alert(response.data.message);
  return false;
}

async function cancelSubscription() {
  // create request to server
  const authToken = localStorage.userSession;
  const instance = axios.create({
    baseURL: `${url}`,
    headers: {
      "Access-Control-Allow-Origin": "*",
      Authorization: `Bearer ${authToken}`
    },
    withCredentials: true,
    credentials: "same-origin"
  });
  const response = await instance.put('/credit/subscription');
  console.log(response);
  if (response.data.success === true) return true;
  else return false;
}

async function findOffer(offer_id) {
  const response = await axios.get(`${url}/credit/offer/${offer_id}`);
  if (response?.data?.success === true) {
    return response.data.product;
  } else return false;
}

async function redeemOffer(email, offer_id) {
  const attribution = _readAttribution();
  const response = await axios.post(`${url}/credit/offer`, { email, offer_id, ...(attribution ? { attribution } : {}) });
  if (response?.data?.success === true) {
    window.location.href = response.data.url;
  } else if (response?.data?.message) alert(response.data.message);
  else alert('This email address has not be registered with us.');
}

/***********  General functions ***********/

// Submit a contact form (the FAQs form and the footer newsletter).
// Resolves 'success' only when the backend says it saved the message, 'invalid' on a 400
// (a field it won't take), otherwise 'error' (not saved: 503, 429, network).
async function sendContact(name, email, message) {
  const data = {
    name,
    email,
    message,
  };
  try {
    const response = await axios.post(`${url}/register/contact`, data);
    return response && response.data && response.data.success === true ? 'success' : 'error';
  } catch (err) {
    return err && err.response && err.response.status === 400 ? 'invalid' : 'error';
  }
}

// Submit a franchise enquiry from /franchise.
//
// Recipients are handled server-side: every enquiry must reach both
// franchise@gympods.com (the live inbox) and test@gympods.com (a
// verification copy while the flow is being validated). Those addresses
// live on the /register/franchise handler, not here — the client never
// names recipients, which keeps this endpoint from being usable as an
// open relay.
//
// Returns true only on a confirmed 2xx so the caller fires its GA4
// conversion solely on real success and shows a visible failure (with a
// mailto fallback) otherwise — enquiries are never silently dropped.
async function sendFranchiseEnquiry(payload) {
  const response = await axios.post(`${url}/register/franchise`, payload)
    .catch(() => false);
  return (response?.status === 200 || response?.status === 201) && response?.data?.success === true;
}

// Get paginated booking history
async function getBookingHistory(page = 1) {
  const authToken = localStorage.userSession;
  console.log(url)
  const response = await axios.get(`${url}/register/bookings/history?page=${page}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`
    }
  }).catch((err) => {
    console.error('Error fetching booking history:', err);
    return null;
  });
  if (response && response.status === 200) {
    return response.data;
  }
  return null;
}

export {
  login,
  register,
  checkEmailRegistered,
  startSession,
  saveSignIn,
  keptSignedIn,
  restoreSignIn,
  verifyEmailLink,
  resendVerificationFromLink,
  resendVerification,
  getProfile,
  loadAccount,
  clearAccount,
  updateProfile,
  logout,
  forgotPassword,
  resetPassword,
  deleteAccount,
  createPTProfile,
  getPTProfile,
  exportVisits,
  getPTListings,
  contactPT,
  claimFreePtSession,
  submitProApplication,
  submitListingUpdate,
  buildLegacyBio,
  LEGACY_BIO_MAX,
  getAvailability,
  bookPod,
  joinWaitingList,
  cancelPod,
  addBuddyToBooking,
  createCheckout,
  pushPurchaseOnce,
  cancelSubscription,
  findOffer,
  redeemOffer,
  sendContact,
  sendFranchiseEnquiry,
  getBookingHistory
};